<template>
  <b-modal
    id="modal-add-hardware"
    ref="modal"
    title="Add hardware"
    hide-footer
    size="xl"
    @hidden="closeModal"
  >
    <b-card-body>
      <b-row
        v-if="$can('create', 'hardware')"
        class="d-flex justify-content-between align-items-center mt-2 mb-2"
      >
        <p>
          You can add hardware from your inventory to this site. Or you can create new hardware.
        </p>
        <b-button
          variant="primary"
          :to="`/hardware/create?siteId=${$route.params.id}`"
        >
          Create new hardware
        </b-button>
      </b-row>
      <!-- table -->
      <vue-good-table
        ref="VueGoodTable"
        :columns="fields"
        :rows="items"
        mode="remote"
        :is-loading="isLoading"
        compact-mode
        :sort-options="{
          enabled: true,
          multipleColumns: true,
          initialSortBy: { field: orderBy, type: sortedBy }
        }"
        @on-sort-change="onSortChange"
        @on-column-filter="columnFilterFn"
      >
        <template slot="loadingContent">
          <b-spinner
            variant="primary"
            label="Spinning"
          />
        </template>
        <template
          slot="table-row"
          slot-scope="props"
        >
          <span v-if="props.column.field === 'action'">
            <b-button-request
              v-if="!successfullyUploadedIds.find(itemId => itemId === props.row.id)"
              class="d-flex align-items-center w-100 justify-content-center"
              :disabled="disabledSaveButton"
              text="Add Hardware"
              :request="() => addHardwareToSite(props.row.id)"
            />
            <div v-else>
              <b-button
                type="submit"
                class="d-flex align-items-center w-100 justify-content-center"
                variant="success"
                disabled
              >
                Hardware added
              </b-button>
            </div>
          </span>
        </template>
      </vue-good-table>
    </b-card-body>
  </b-modal>
</template>

<script>
import {
  BCardBody, BSpinner, BModal, VBTooltip, BButton,
} from 'bootstrap-vue'
import { mapState, mapActions } from 'vuex'
import { VueGoodTable } from 'vue-good-table'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import IndexPagesMixin from '@/mixins/IndexPages.vue'
import BButtonRequest from '../../../../components/BButtonRequest.vue'

export default {
  directives: {
    'b-tooltip': VBTooltip,
  },
  components: {
    BButtonRequest,
    BCardBody,
    BSpinner,
    BButton,
    BModal,
    VueGoodTable,
  },
  mixins: [
    IndexPagesMixin,
  ],
  props: {
    update: {
      type: Function,
      required: true,
    },
    addedItems: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      updatingIdList: [],
      successfullyUploadedIds: [],
      disabledSaveButton: false,
      fields: [
        {
          field: 'name',
          label: 'Name',
          filterOptions: {
            enabled: true,
            placeholder: 'Filter',
            filterValue: '',
          },
        },
        {
          field: 'model.name',
          label: 'Model',
          filterOptions: {
            enabled: true,
            placeholder: 'Filter',
            filterValue: '',
          },
        },
        {
          field: 'licence',
          label: 'Licence',
          filterOptions: {
            enabled: true,
            placeholder: 'Filter',
            filterValue: '',
          },
        },
        {
          field: 'serial_number',
          label: 'Serial No.',
          sortable: false,
          filterOptions: {
            enabled: true,
            placeholder: 'Filter',
            filterValue: '',
          },
        },
        {
          field: 'warranty_expiry_at',
          label: 'Warranty Expiry',
          filterOptions: {
            enabled: true,
            placeholder: 'Filter',
            filterValue: '',
          },
        },
        {
          field: 'comment',
          label: 'Comment',
          filterOptions: {
            enabled: true,
            placeholder: 'Filter',
            filterValue: '',
          },
        },
        {
          field: 'ip_address',
          label: 'IP address',
          sortable: false,
          filterOptions: {
            enabled: true,
            placeholder: 'Filter',
            filterValue: '',
          },
        },
        {
          field: 'action',
          label: 'Action',
          sortable: false,
          thClass: 'add-hardware-to-site-action-th-width',
          hidden: !this.$can('update', 'users'),
        },
      ],
      isLoading: true,
    }
  },
  computed: {
    ...mapState('status', {
      items: 'availableToSiteItems',
      searchFields: 'searchFields',
      orderBy: 'orderBy',
      sortedBy: 'sortedBy',
    }),
  },
  watch: {
    addedItems(items) {
      console.log(items)

      this.getData()
    },
  },
  async mounted() {
    await this.getData()
  },
  methods: {
    ...mapActions('status', {
      fetchItems: 'getAvailableToSiteItems',
      setHardwaresToSite: 'setHardwaresToSite',
      setSearchFields: 'setSearchFields',
      setOrderBy: 'setOrderBy',
      setSortedBy: 'setSortedBy',
    }),
    async addHardwareToSite(id) {
      this.disabledSaveButton = true
      if (this.successfullyUploadedIds.includes(id) || this.updatingIdList.includes(id)) {
        return
      }
      this.updatingIdList.push(id)
      try {
        await this.setHardwaresToSite({ siteId: this.$route.params.id, hardwareIds: [id] })
        this.successfullyUploadedIds.push(id)
      } catch (error) {
        const { status } = error.response
        if (status === 404) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Hardware not found',
              icon: 'BellIcon',
              variant: 'danger',
            },
          })
        }
      } finally {
        this.updatingIdList = this.updatingIdList.filter(itemId => itemId !== id)
        this.disabledSaveButton = false
      }
    },
    async closeModal() {
      await this.update()
      this.successfullyUploadedIds = []
      this.updatingIdList = []
      this.getData()
      this.$refs.modal.hide()
    },
    async getData() {
      await this.fetchItems()
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';

.add-hardware-to-site-action-th-width {
  width: 190px !important;
}

.table-selected-row-info-panel {
  display: flex;
  align-items: center;
  padding: 10px;

  a {
    color: red
  }
}

.vgt-pull-right {
  float: unset !important;
  margin-left: auto;
}

.dark-layout {
  .vgt-select {
    background-color: #283046;
    border-color: #404656;
  }

  .table-selected-row-info-panel {
    background-color: #283046;
    border-color: #404656;
  }
}
</style>
